<template>
  <div class="news-admin">
    <div class="toolbar">
      <div class="toolbar-left">
        <input
          v-model.trim="keyword"
          type="search"
          class="search-input"
          placeholder="搜索标题或内容…"
          @keyup.enter="loadList"
        />
        <button type="button" class="btn btn-secondary" @click="loadList">搜索</button>
        <button v-if="keyword" type="button" class="btn btn-ghost" @click="resetSearch">重置</button>
      </div>
      <button type="button" class="btn btn-primary" @click="openCreate">+ 创建新闻</button>
    </div>

    <p v-if="error" class="alert alert-error">{{ error }}</p>
    <p v-if="success" class="alert alert-success">{{ success }}</p>

    <div v-if="loading" class="loading">加载中…</div>

    <div v-else class="table-wrap">
      <table class="data-table">
        <thead>
          <tr>
            <th>标题</th>
            <th class="col-desc">内容摘要</th>
            <th class="col-time">更新时间</th>
            <th class="col-actions">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!items.length">
            <td colspan="4" class="empty-cell">暂无新闻，点击「创建新闻」添加</td>
          </tr>
          <tr v-for="row in items" :key="row.id">
            <td class="col-title">{{ row.title }}</td>
            <td class="col-desc">
              <span class="desc-text" :title="row.content">{{ contentPreview(row.content) }}</span>
            </td>
            <td class="col-time">{{ formatTime(row.updated_at) }}</td>
            <td class="col-actions">
              <button type="button" class="btn-link" @click="openEdit(row)">编辑</button>
              <button type="button" class="btn-link danger" @click="confirmDelete(row)">删除</button>
            </td>
          </tr>
        </tbody>
      </table>
      <p class="table-footer">共 {{ items.length }} 条 · 按更新时间倒序</p>
    </div>

    <div v-if="modalVisible" class="modal-mask" @click.self="closeModal">
      <div class="modal modal-wide">
        <h3 class="modal-title">{{ editingId ? '编辑新闻' : '创建新闻' }}</h3>
        <form class="modal-form" @submit.prevent="submitForm">
          <div class="form-row">
            <label>标题 <span class="req">*</span></label>
            <input v-model.trim="form.title" type="text" class="input-lg" required />
          </div>
          <div class="form-row">
            <label>内容 <span class="req">*</span></label>
            <textarea
              v-model.trim="form.content"
              class="textarea-lg"
              rows="12"
              wrap="off"
              placeholder="按 Enter 换行；段落之间空一行。长句不会自动折行，便于与前台显示一致"
              required
            />
          </div>
          <p v-if="formError" class="alert alert-error">{{ formError }}</p>
          <div class="modal-actions">
            <button type="button" class="btn btn-ghost" @click="closeModal">取消</button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              {{ saving ? '保存中…' : '保存' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import {
  createNewsArticle,
  deleteNewsArticle,
  fetchNewsArticles,
  updateNewsArticle
} from '@/api/news'

const emptyForm = () => ({
  title: '',
  content: ''
})

export default {
  name: 'AdminNews',
  data() {
    return {
      items: [],
      keyword: '',
      loading: false,
      saving: false,
      error: '',
      success: '',
      modalVisible: false,
      editingId: null,
      form: emptyForm(),
      formError: ''
    }
  },
  created() {
    this.loadList()
  },
  methods: {
    contentPreview(content) {
      const text = (content || '').replace(/\s+/g, ' ').trim()
      if (!text) return '—'
      return text.length > 80 ? `${text.slice(0, 80)}…` : text
    },
    formatTime(value) {
      if (!value) return '—'
      const d = new Date(value.replace(' ', 'T'))
      if (Number.isNaN(d.getTime())) return value
      return d.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    async loadList() {
      this.loading = true
      this.error = ''
      try {
        const res = await fetchNewsArticles(this.keyword)
        this.items = res.data?.items || []
      } catch (e) {
        this.error = e.message || '加载失败'
      } finally {
        this.loading = false
      }
    },
    resetSearch() {
      this.keyword = ''
      this.loadList()
    },
    openCreate() {
      this.editingId = null
      this.form = emptyForm()
      this.formError = ''
      this.modalVisible = true
    },
    openEdit(row) {
      this.editingId = row.id
      this.form = {
        title: row.title,
        content: row.content || ''
      }
      this.formError = ''
      this.modalVisible = true
    },
    closeModal() {
      this.modalVisible = false
      this.formError = ''
    },
    async submitForm() {
      this.formError = ''
      if (!this.form.title || !this.form.content) {
        this.formError = '请填写标题和内容'
        return
      }
      this.saving = true
      try {
        const payload = {
          title: this.form.title,
          content: this.form.content
        }
        if (this.editingId) {
          await updateNewsArticle(this.editingId, payload)
          this.success = '新闻已更新'
        } else {
          await createNewsArticle(payload)
          this.success = '新闻已创建'
        }
        this.closeModal()
        await this.loadList()
        setTimeout(() => {
          this.success = ''
        }, 2500)
      } catch (e) {
        this.formError = e.message || '保存失败'
      } finally {
        this.saving = false
      }
    },
    confirmDelete(row) {
      if (!window.confirm(`确定删除新闻「${row.title}」？`)) return
      this.doDelete(row.id)
    },
    async doDelete(id) {
      this.error = ''
      try {
        await deleteNewsArticle(id)
        this.success = '已删除'
        await this.loadList()
        setTimeout(() => {
          this.success = ''
        }, 2500)
      } catch (e) {
        this.error = e.message || '删除失败'
      }
    }
  }
}
</script>

<style scoped>
.news-admin {
  max-width: 1100px;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.toolbar-left {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.search-input {
  width: 260px;
  padding: 8px 12px;
  font-size: 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.btn {
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 8px;
  border: none;
  cursor: pointer;
}

.btn-primary {
  background: #0d9488;
  color: #fff;
}

.btn-primary:hover {
  background: #0f766e;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: #fff;
  color: #334155;
  border: 1px solid #e2e8f0;
}

.btn-ghost {
  background: transparent;
  color: #64748b;
}

.alert {
  margin: 0 0 12px;
  padding: 10px 14px;
  font-size: 13px;
  border-radius: 8px;
}

.alert-error {
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
}

.alert-success {
  color: #047857;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
}

.loading {
  padding: 40px;
  text-align: center;
  color: #64748b;
}

.table-wrap {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
  overflow: hidden;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.data-table th,
.data-table td {
  padding: 12px 14px;
  text-align: left;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: top;
}

.data-table th {
  font-weight: 600;
  color: #475569;
  background: #f8fafc;
  font-size: 13px;
}

.col-title {
  font-weight: 500;
  color: #0f172a;
}

.col-desc {
  max-width: 420px;
}

.desc-text {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
  color: #475569;
}

.col-time {
  width: 150px;
  white-space: nowrap;
  color: #64748b;
  font-size: 13px;
}

.col-actions {
  width: 100px;
  white-space: nowrap;
}

.btn-link {
  margin-right: 10px;
  padding: 0;
  font-size: 13px;
  color: #0d9488;
  background: none;
  border: none;
  cursor: pointer;
}

.btn-link.danger {
  color: #dc2626;
}

.empty-cell {
  text-align: center;
  color: #94a3b8;
  padding: 32px !important;
}

.table-footer {
  margin: 0;
  padding: 10px 14px;
  font-size: 12px;
  color: #94a3b8;
}

.modal-mask {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.45);
}

.modal {
  width: 100%;
  max-width: 720px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 28px 32px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.15);
}

.modal-wide {
  max-width: 760px;
}

.modal-title {
  margin: 0 0 20px;
  font-size: 18px;
  color: #0f172a;
}

.modal-form label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

.req {
  color: #dc2626;
}

.modal-form input,
.modal-form textarea {
  width: 100%;
  box-sizing: border-box;
  padding: 9px 12px;
  font-size: 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.modal-form .input-lg {
  padding: 12px 14px;
  font-size: 15px;
}

.modal-form .textarea-lg {
  min-height: 220px;
  padding: 12px 14px;
  font-size: 15px;
  line-height: 1.6;
  resize: vertical;
  white-space: pre;
  overflow-x: auto;
}

.form-row {
  margin-bottom: 16px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>
